import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CalculatorShell, { Field, Result, inputClass } from "@/components/CalculatorShell";
import { getCalculator } from "@/data/calculators";
import { redundancyPay, gbp, longDate, TERMINATION_TAX_FREE } from "@/lib/employment-calcs";

const calc = getCalculator("redundancy-pay-calculator");

const relevantNote = (r) =>
  r.relevant.extended
    ? `Relevant date: ${longDate(r.relevant.relevantDate)}. Less than the statutory ${r.relevant.statutoryNoticeWeeks} week${r.relevant.statutoryNoticeWeeks === 1 ? "'s" : "s'"} notice was given, so the date moves to when statutory notice would have ended (s145(5)).`
    : `Relevant date: ${longDate(r.relevant.relevantDate)}, the last day of employment.`;

export default function RedundancyPayCalculator() {
  const [form, setForm] = useState({ dateOfBirth: "", startDate: "", endDate: "", noticeGivenDate: "", weeklyPay: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready = form.dateOfBirth && form.startDate && form.endDate && form.weeklyPay !== "";
  const r = useMemo(() => (ready ? redundancyPay(form) : null), [form, ready]);

  return (
    <CalculatorShell
      calc={calc}
      howItWorks={
        <>
          <p>
            An employee needs <strong>two years' continuous employment</strong>, ending with the date their employment ends,
            to qualify (Employment Rights Act 1996, s155).
          </p>
          <p>
            Everything is measured to the <strong>relevant date</strong>: normally the day employment ends. If you gave
            less notice than the statutory minimum, or none (for example paying in lieu), the relevant date moves to when
            statutory notice would have run out (s145(5)). That later date decides whether the employee has two years'
            service, how many years and which age bands count, and which weekly cap applies.
          </p>
          <p>
            Count back <strong>complete years</strong> of service from that date, up to a maximum of 20. Each year earns a
            number of weeks' pay depending on the employee's age during that year (s162):
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>half a week's pay for each year when they were under 22</li>
            <li>one week's pay for each year when they were 22 to 40</li>
            <li>one and a half weeks' pay for each year when they were 41 or over</li>
          </ul>
          <p>
            A week's pay is gross pay for normal working hours, averaged over 12 weeks if it varies, and capped at{" "}
            <strong>£751</strong> where the relevant date is on or after 6 April 2026 (£719 for the year before). That makes the
            maximum statutory payment <strong>£22,530</strong>.
          </p>
          <p>
            Up to {gbp(TERMINATION_TAX_FREE.amount)} of statutory and other non-contractual termination payments is tax-free. Notice
            pay and holiday pay are taxed as normal. To split a whole leaving package into taxed and tax-free parts, use our{" "}
            <Link to="/calculators/redundancy-package-tax-calculator" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">redundancy package and tax calculator</Link>.
          </p>
        </>
      }
      example={
        <p>
          Priya is 45 and has worked for you for 16 years. Her pay is £800 a week. Her last 5 years were all at 41 or
          over (5 × 1.5 = 7.5 weeks) and the 11 years before that at 22 to 40 (11 weeks), so 18.5 weeks. Her pay is
          capped at £751, giving <strong>£13,893.50</strong>.
        </p>
      }
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Employee's date of birth" htmlFor="dob">
          <input id="dob" type="date" className={inputClass} value={form.dateOfBirth} onChange={set("dateOfBirth")} data-testid="red-dob" />
        </Field>
        <Field label="Date employment started" htmlFor="start">
          <input id="start" type="date" className={inputClass} value={form.startDate} onChange={set("startDate")} data-testid="red-start" />
        </Field>
        <Field label="Date notice was given" hint="Leave blank if you gave no notice or paid in lieu." htmlFor="noticegiven">
          <input id="noticegiven" type="date" className={inputClass} value={form.noticeGivenDate} onChange={set("noticeGivenDate")} data-testid="red-notice" />
        </Field>
        <Field label="Last day of employment" hint="The day notice runs out, or the actual leaving date if paid in lieu." htmlFor="end">
          <input id="end" type="date" className={inputClass} value={form.endDate} onChange={set("endDate")} data-testid="red-end" />
        </Field>
        <Field label="Weekly pay before tax (£)" hint="Monthly salary × 12 ÷ 52, or the 12-week average if pay varies." htmlFor="pay">
          <input id="pay" type="number" min="0" step="0.01" inputMode="decimal" className={inputClass} value={form.weeklyPay} onChange={set("weeklyPay")} data-testid="red-pay" />
        </Field>
      </div>

      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && !r.eligible && (
        <Result
          items={[{ label: "Statutory redundancy pay", value: gbp(0), big: true, sub: `${r.yearsOfService} complete year${r.yearsOfService === 1 ? "" : "s"} of service` }]}
          notes={[
            relevantNote(r),
            "Statutory redundancy pay needs at least two years' continuous employment by the relevant date. Contractual or enhanced terms may still give something.",
          ]}
        />
      )}
      {r && !r.error && r.eligible && (
        <Result
          items={[
            { label: "Statutory redundancy pay", value: gbp(r.total), big: true, sub: `${r.weeks} weeks × ${gbp(r.weeklyPayUsed)}` },
            { label: "Years counted", value: `${r.yearsCounted}`, sub: r.yearsOfService > 20 ? `of ${r.yearsOfService} (20-year limit)` : "complete years" },
            { label: "Weekly pay used", value: gbp(r.weeklyPayUsed), sub: r.capped ? `capped at ${gbp(r.cap)} (${r.capSource})` : `cap is ${gbp(r.cap)}` },
          ]}
          notes={[
            relevantNote(r),
            `Years at 41 or over: ${r.breakdown.age41plus} × 1.5 weeks. Years at 22 to 40: ${r.breakdown.age22to40} × 1 week. Years under 22: ${r.breakdown.under22} × 0.5 week.`,
            r.taxFree
              ? "Statutory redundancy pay of this size is tax-free, as long as other non-contractual termination payments don't take the total over £30,000."
              : "Over £30,000 of non-contractual termination payments is taxable, with employer Class 1A National Insurance on the excess.",
            "Notice pay, holiday pay and wages are paid on top and taxed as normal.",
          ]}
        />
      )}
    </CalculatorShell>
  );
}
